import styles from './LegalPage.module.css';

const TermsPage = () => {
  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1>Terms of Use</h1>
        <p className={styles.lastUpdated}>Last Updated: April 2, 2026</p>

        <section>
          <p>
            Welcome to Oval Care, Inc. ("Oval Care," "Company," "we," "us," or "our"). These Terms of Use ("Terms") govern your access to and use of our website located at <a href="https://ovalcare.com">https://ovalcare.com</a> and any related applications, services, or content (collectively, the "Site"). By accessing or using the Site, you agree to be bound by these Terms and our Privacy Policy. If you do not agree, please do not use the Site.
          </p>
        </section>

        <section>
          <h2>1. Medical Information and Disclaimers</h2>
          <p>
            <strong>A. Not Medical Advice:</strong> The Site provides information about healthcare products and telemedicine services but does not provide medical advice, diagnosis, or treatment. All medical decisions are made solely by licensed healthcare providers. Oval Care does not practice medicine or provide clinical services.
          </p>
          <p>
            <strong>B. No Doctor-Patient Relationship:</strong> Your use of the Site does not create a doctor-patient relationship with Oval Care. Any medical services, consultations, or prescriptions you receive through the Site are provided by independent, licensed physicians or healthcare professionals.
          </p>
          <p>
            <strong>C. Reliance on Information:</strong> You should not rely solely on information provided on the Site. Always consult with a qualified healthcare provider before making any healthcare decisions or taking action based on content you access through the Site.
          </p>
          <p>
            <strong>D. Emergency Services:</strong> The Site is not intended for use in medical emergencies. If you are experiencing a medical emergency, call 911 or go to your nearest emergency room immediately.
          </p>
        </section>

        <section>
          <h2>2. Telemedicine Services</h2>
          <p>
            <strong>A. Third-Party Providers:</strong> Telemedicine consultations available through the Site are provided by independent healthcare professionals or third-party telemedicine platforms, not by Oval Care. Oval Care facilitates access to these services but does not control, supervise, or manage the care you receive.
          </p>
          <p>
            <strong>B. Licensure and Credentials:</strong> Healthcare providers accessible through the Site are licensed and authorized to practice in the jurisdictions where they provide services. It is your responsibility to verify that the provider is licensed in your state before receiving care.
          </p>
          <p>
            <strong>C. Prescription Services:</strong> Prescriptions are issued at the sole discretion of the prescribing healthcare provider. Oval Care does not guarantee that a prescription will be issued or that any particular treatment will be recommended.
          </p>
          <p>
            <strong>D. Limitations of Telemedicine:</strong> Telemedicine has inherent limitations compared to in-person consultations. The healthcare provider may not be able to perform a physical examination or conduct certain diagnostic tests. You understand and accept these limitations when using telemedicine services through the Site.
          </p>
        </section>

        <section>
          <h2>3. State-Specific Notices</h2>
          <p>
            <strong>A. California Residents:</strong> Under California law, patients have the right to request a copy of their medical records and to receive a written disclosure of the name, professional degree, and license number of their treating physician. For questions or to request this information, contact <a href="mailto:support@ovalcare.com">support@ovalcare.com</a>.
          </p>
          <p>
            <strong>B. New York Residents:</strong> New York law requires that all telemedicine encounters be conducted via real-time, two-way audio-video communication. Prescriptions may not be issued based solely on questionnaires or asynchronous messaging.
          </p>
          <p>
            <strong>C. Texas Residents:</strong> Texas law requires that physicians conducting telemedicine consultations maintain minimum standards of care equivalent to in-person consultations. Physicians must establish a proper provider-patient relationship before prescribing controlled substances.
          </p>
          <p>
            <strong>D. Other States:</strong> Depending on your state of residence, additional restrictions or requirements may apply to telemedicine services. It is your responsibility to ensure compliance with your local laws.
          </p>
        </section>

        <section>
          <h2>4. Eligibility</h2>
          <p>
            You must be at least 18 years old to use the Site and access services. By using the Site, you represent and warrant that you are at least 18 years old and that all information you provide is accurate, current, and complete. If you are under 18, you may not use the Site.
          </p>
        </section>

        <section>
          <h2>5. User Accounts and Registration</h2>
          <p>
            <strong>A. Account Creation:</strong> To access certain features of the Site, you may be required to create an account. You agree to provide accurate, current, and complete information during registration and to update your information as necessary.
          </p>
          <p>
            <strong>B. Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. Notify us immediately at <a href="mailto:support@ovalcare.com">support@ovalcare.com</a> if you believe your account has been compromised.
          </p>
          <p>
            <strong>C. Account Suspension or Termination:</strong> We reserve the right to suspend or terminate your account at any time, without notice, if we believe you have violated these Terms or engaged in fraudulent or harmful conduct.
          </p>
        </section>

        <section>
          <h2>6. Prohibited Conduct</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Site for any unlawful purpose or in violation of these Terms;</li>
            <li>Misrepresent your identity or provide false information;</li>
            <li>Attempt to gain unauthorized access to any part of the Site, other user accounts, or computer systems;</li>
            <li>Interfere with or disrupt the operation of the Site or servers;</li>
            <li>Upload or transmit viruses, malware, or other harmful code;</li>
            <li>Harvest, scrape, or collect information about other users without their consent;</li>
            <li>Use automated tools (bots, scripts) to access or interact with the Site;</li>
            <li>Reverse engineer, decompile, or attempt to extract source code from the Site;</li>
            <li>Post or transmit defamatory, obscene, abusive, or otherwise objectionable content;</li>
            <li>Violate any applicable local, state, national, or international law.</li>
          </ul>
        </section>

        <section>
          <h2>7. Intellectual Property</h2>
          <p>
            <strong>A. Ownership:</strong> The Site and its contents, including but not limited to text, graphics, logos, images, software, and trademarks, are owned by Oval Care or its licensors and are protected by copyright, trademark, and other intellectual property laws.
          </p>
          <p>
            <strong>B. Limited License:</strong> We grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Site for personal, non-commercial purposes. You may not copy, modify, distribute, sell, or create derivative works from any part of the Site without our prior written consent.
          </p>
          <p>
            <strong>C. Trademarks:</strong> "Oval Care" and other marks displayed on the Site are trademarks of Oval Care, Inc. or its affiliates. You may not use these marks without our express written permission.
          </p>
        </section>

        <section>
          <h2>8. Third-Party Links and Services</h2>
          <p>
            The Site may contain links to third-party websites, applications, or services. We do not control, endorse, or assume responsibility for the content or practices of third parties. Your use of third-party services is at your own risk and subject to the terms and policies of those third parties.
          </p>
        </section>

        <section>
          <h2>9. Payment and Billing</h2>
          <p>
            <strong>A. Fees:</strong> Certain services or products available through the Site may require payment. All fees are stated on the Site and are subject to change without notice. You agree to pay all applicable fees, including taxes and shipping charges.
          </p>
          <p>
            <strong>B. Payment Methods:</strong> We accept major credit cards and other forms of payment as displayed on the Site. By providing payment information, you represent that you are authorized to use the payment method.
          </p>
          <p>
            <strong>C. Recurring Charges:</strong> If you subscribe to a membership or recurring service, you authorize us to charge your payment method automatically at the agreed-upon intervals until you cancel. Cancellation instructions are provided on your account page.
          </p>
          <p>
            <strong>D. Refunds:</strong> All sales are final unless otherwise stated. Please review our Refund and Return Policy for more details.
          </p>
        </section>

        <section>
          <h2>10. Disclaimers and Warranties</h2>
          <p>
            <strong>THE SITE AND ALL CONTENT AND SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.</strong>
          </p>
          <p>
            We do not warrant that:
          </p>
          <ul>
            <li>The Site will be available at all times or free from errors, interruptions, or defects;</li>
            <li>The information provided on the Site is accurate, complete, or reliable;</li>
            <li>Any defects in the Site will be corrected;</li>
            <li>The Site or servers are free from viruses or other harmful components.</li>
          </ul>
          <p>
            Use of the Site and any services is at your own risk. You are solely responsible for any damage to your device or loss of data resulting from your use of the Site.
          </p>
        </section>


        <section>
          <h2>11. No Medical Services</h2>
          <p>
            Oval Care, Inc. is a technology platform that facilitates connections between users and independent, licensed healthcare providers. Oval Care does not provide medical services, diagnoses, treatments, or prescriptions. Oval Care is not a healthcare provider, medical practice, pharmacy, or insurance company.
          </p>
          <p>
            All medical services available through the Site are performed by independent healthcare providers who are solely responsible for the care they deliver. Oval Care does not supervise, direct, or control the clinical decisions or medical judgment of any provider.
          </p>
          <p>
            Nothing on the Site should be construed as the practice of medicine, nursing, pharmacy, or any other healthcare profession by Oval Care.
          </p>
        </section>

        <section>
          <h2>12. No Doctor-Patient Relationship</h2>
          <p>
            Your use of the Oval Care platform does not create a doctor-patient relationship between you and Oval Care. Any doctor-patient relationship that arises through the use of the Site exists solely between you and the independent healthcare provider who delivers your care.
          </p>
          <p>
            Oval Care is not a party to and bears no responsibility for the doctor-patient relationship between you and any provider. The provider is solely responsible for all medical decisions, consultations, prescriptions, and treatments related to your care.
          </p>
          <p>
            You acknowledge that Oval Care does not make any representations or warranties regarding the qualifications, competence, or quality of care of any healthcare provider accessible through the Site.
          </p>
        </section>

        <section>
          <h2>13. Independent Providers</h2>
          <p>
            All healthcare providers accessible through the Oval Care platform are independent contractors. They are not employees, agents, or representatives of Oval Care. Oval Care does not employ, supervise, direct, or control any healthcare provider's clinical decisions, medical practices, or professional conduct.
          </p>
          <p>
            Each healthcare provider is independently licensed and solely responsible for the services they provide, including but not limited to consultations, diagnoses, prescriptions, and treatment recommendations. Oval Care does not credential, endorse, or guarantee the qualifications, competence, or performance of any provider.
          </p>
          <p>
            Any claims, disputes, or grievances arising from the care you receive should be directed to the provider who delivered that care. Oval Care is not liable for any acts, omissions, errors, or negligence of any healthcare provider.
          </p>
          <p>
            All liability arising from or related to any medical services, consultations, diagnoses, prescriptions, treatments, or outcomes shall rest solely and exclusively with the treating healthcare provider. Oval Care shall have no responsibility or liability for any such medical services or outcomes under any circumstances.
          </p>
        </section>

        <section>
          <h2>14. Prescription Disclaimer</h2>
          <p>
            Prescriptions are not guaranteed. The decision to prescribe medication is made solely by the independent healthcare provider based on their clinical assessment and medical judgment. A provider may decline to issue a prescription for any reason, including but not limited to medical contraindications, incomplete health information, regulatory restrictions, or the provider's professional assessment that the requested treatment is not appropriate.
          </p>
          <p>
            Oval Care does not influence, direct, or control any provider's prescribing decisions. Oval Care makes no representations or warranties that any particular medication, dosage, or treatment will be prescribed or made available to you.
          </p>
          <p>
            If a prescription is issued, it is subject to verification and fulfillment by an independent, licensed pharmacy. The pharmacy may contact you or the prescribing provider for additional information and reserves the right to decline to fill any prescription in accordance with applicable laws and professional standards.
          </p>
          <p>
            Oval Care makes no representations or warranties regarding any medical outcomes, the effectiveness of any treatment, or the results of any prescription or healthcare service. All medical outcomes are solely dependent on the independent healthcare provider and individual patient circumstances.
          </p>
        </section>

        <section>
          <h2>15. Assumption of Risk</h2>
          <p>
            By using the Oval Care platform and any services facilitated through the Site, you acknowledge and accept the inherent risks associated with healthcare services, telemedicine consultations, and the use of prescription medications. These risks include, but are not limited to:
          </p>
          <ul>
            <li>Potential side effects, adverse reactions, and allergic reactions to prescribed medications;</li>
            <li>Drug interactions with other medications, supplements, or substances you may be using;</li>
            <li>Limitations of telemedicine consultations, including the inability to perform physical examinations;</li>
            <li>Risks associated with providing inaccurate or incomplete health information to healthcare providers;</li>
            <li>Delays in receiving care, prescriptions, or medications due to technical issues, provider availability, or other factors beyond Oval Care's control.</li>
          </ul>
          <p>
            You assume full responsibility for your decision to use the services and understand that Oval Care does not guarantee any particular medical outcome. You are solely responsible for following all instructions provided by your healthcare provider, including dosage instructions, follow-up care, and reporting any adverse effects.
          </p>
        </section>

        <section>
          <h2>16. Release of Liability</h2>
          <p>
            To the fullest extent permitted by applicable law, you hereby release, waive, and forever discharge Oval Care, Inc., its officers, directors, employees, affiliates, agents, successors, and assigns from any and all claims, demands, causes of action, damages, losses, costs, and expenses (including attorneys' fees) arising out of or relating to:
          </p>
          <ul>
            <li>Any medical services, consultations, prescriptions, or treatments you receive through healthcare providers accessible via the Site;</li>
            <li>Any acts, omissions, errors, negligence, or malpractice of any healthcare provider;</li>
            <li>Any adverse reactions, side effects, drug interactions, or other consequences of medications prescribed or dispensed through the platform;</li>
            <li>Any decisions made by healthcare providers regarding your care, including the decision to prescribe or not prescribe medication;</li>
            <li>Any failure or delay in the delivery of prescriptions or other services by third-party pharmacies or logistics providers.</li>
          </ul>
          <p>
            This release applies to claims of any kind, whether known or unknown, suspected or unsuspected, disclosed or undisclosed, arising at any time. You expressly waive any rights under any statute or legal doctrine that would otherwise limit the scope of this release to claims known or suspected at the time of execution.
          </p>
        </section>

        <section>
          <h2>17. Limitation of Liability</h2>
          <p>
            <strong>TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, OVAL CARE, INC., ITS OFFICERS, DIRECTORS, EMPLOYEES, AFFILIATES, AGENTS, SUCCESSORS, AND ASSIGNS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, GOODWILL, OR USE, ARISING OUT OF OR RELATED TO YOUR USE OF THE SITE OR ANY SERVICES FACILITATED THROUGH THE SITE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.</strong>
          </p>
          <p>
            <strong>WITHOUT LIMITING THE FOREGOING, OVAL CARE EXPRESSLY DISCLAIMS AND SHALL NOT BE LIABLE FOR ANY DAMAGES, LOSSES, OR CLAIMS ARISING FROM OR RELATED TO:</strong>
          </p>
          <ul>
            <li>Any medical services, consultations, diagnoses, prescriptions, or treatments provided by independent healthcare providers;</li>
            <li>Any adverse reactions, side effects, drug interactions, allergic reactions, or other medical complications resulting from medications prescribed or dispensed through the platform;</li>
            <li>Any acts, omissions, errors, negligence, or malpractice of any healthcare provider, pharmacy, or other third party;</li>
            <li>Any lost profits, lost revenue, or economic damages of any kind;</li>
            <li>Any consequential, incidental, or indirect damages, regardless of the theory of liability.</li>
          </ul>
          <p>
            In no event shall Oval Care's total aggregate liability to you for all claims arising out of or related to your use of the Site or any services exceed the lesser of (a) one hundred dollars ($100) or (b) the total amount you actually paid to Oval Care in the twelve (12) months immediately preceding the event giving rise to the claim.
          </p>
          <p>
            The limitations set forth in this section shall apply regardless of the form of action, whether in contract, tort (including negligence), strict liability, or any other legal or equitable theory, and shall survive termination of these Terms.
          </p>
          <p>
            Some states do not allow the exclusion or limitation of certain damages, so some of the above limitations may not apply to you. In such states, Oval Care's liability shall be limited to the fullest extent permitted by applicable law.
          </p>
        </section>

        <section>
          <h2>18. Third-Party Services</h2>
          <p>
            The healthcare providers, pharmacies, laboratories, and other service providers accessible through the Oval Care platform are independent third parties. They are not owned, operated, controlled, or managed by Oval Care. Oval Care does not employ, supervise, or direct the activities of any such third party.
          </p>
          <p>
            Oval Care is not responsible or liable for the actions, omissions, services, products, or conduct of any third-party provider, including but not limited to:
          </p>
          <ul>
            <li>The quality, safety, or efficacy of medical services, consultations, or treatments provided by healthcare professionals;</li>
            <li>The accuracy, completeness, or timeliness of prescriptions issued by healthcare providers;</li>
            <li>The quality, safety, or proper dispensing of medications by pharmacies;</li>
            <li>The accuracy or reliability of any laboratory tests or diagnostic services;</li>
            <li>Any delays, errors, or failures in the delivery of medications or other products by pharmacies, shipping carriers, or logistics providers.</li>
          </ul>
          <p>
            Your use of any third-party services facilitated through the Site is governed by the terms, conditions, and privacy policies of those third parties. Oval Care makes no representations or warranties regarding any third-party services and disclaims all liability arising from your use of such services.
          </p>
          <p>
            Any disputes between you and a third-party provider should be resolved directly with that provider. Oval Care is not a party to and shall not be required to participate in any dispute between you and any third party.
          </p>
        </section>

        <section>
          <h2>19. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless Oval Care, its affiliates, and their respective officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of or related to:
          </p>
          <ul>
            <li>Your use of the Site;</li>
            <li>Your violation of these Terms;</li>
            <li>Your violation of any third-party rights, including intellectual property or privacy rights;</li>
            <li>Any false or misleading information you provide.</li>
          </ul>
        </section>

        <section>
          <h2>20. Arbitration and Dispute Resolution</h2>
          <p>
            <strong>A. Binding Arbitration:</strong> Any dispute, claim, or controversy arising out of or relating to these Terms or your use of the Site shall be resolved by binding arbitration administered by the American Arbitration Association (AAA) under its Commercial Arbitration Rules. The arbitration shall be conducted in Delaware, and judgment on the award may be entered in any court of competent jurisdiction.
          </p>
          <p>
            <strong>B. Exceptions:</strong> Either party may seek injunctive or equitable relief in court to protect intellectual property rights or prevent irreparable harm.
          </p>
          <p>
            <strong>C. Class Action Waiver:</strong> You agree that any arbitration or proceeding shall be conducted on an individual basis and not as a class action, consolidated action, or representative action. You waive your right to participate in a class action lawsuit or class-wide arbitration.
          </p>
          <p>
            <strong>D. Opt-Out:</strong> You may opt out of this arbitration agreement by sending written notice to <a href="mailto:support@ovalcare.com">support@ovalcare.com</a> within thirty (30) days of your first use of the Site. Your notice must include your name, address, and a clear statement that you wish to opt out of arbitration.
          </p>
        </section>

        <section>
          <h2>21. Governing Law</h2>
          <p>
            These Terms and your use of the Site are governed by the laws of the State of Delaware, without regard to its conflict of law principles. You consent to the exclusive jurisdiction of the state and federal courts located in Delaware for any disputes not subject to arbitration.
          </p>
        </section>

        <section>
          <h2>22. Changes to These Terms</h2>
          <p>
            We reserve the right to update or modify these Terms at any time. When we make changes, we will revise the "Last Updated" date at the top of this page. Your continued use of the Site after changes have been posted constitutes your acceptance of the revised Terms.
          </p>
        </section>

        <section>
          <h2>23. Termination</h2>
          <p>
            We may suspend or terminate your access to the Site at any time, with or without cause or notice, if we believe you have violated these Terms or engaged in conduct that is harmful to the Site, other users, or our business.
          </p>
        </section>

        <section>
          <h2>24. Severability</h2>
          <p>
            If any provision of these Terms is found to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect.
          </p>
        </section>

        <section>
          <h2>25. Entire Agreement</h2>
          <p>
            These Terms, together with our Privacy Policy and any other legal notices or policies posted on the Site, constitute the entire agreement between you and Oval Care regarding your use of the Site.
          </p>
        </section>

        <section>
          <h2>26. Contact Us</h2>
          <p>
            If you have any questions or concerns about these Terms, please contact us at:
          </p>
          <address>
            Oval Care, Inc.<br />
            Attn: Patient Care Team<br />
            848 Brickell Avenue PH5,<br />
            Miami, FL 33131<br />
            United States<br />
            Phone: +1 (737) 396‑1791<br />
            Email: <a href="mailto:support@ovalcare.com">support@ovalcare.com</a>
          </address>
        </section>

        <section>
          <h2>27. DMCA Notice</h2>
          <p>
            If you believe that content on the Site infringes your copyright, please send a written notice to our designated Copyright Agent at <a href="mailto:support@ovalcare.com">support@ovalcare.com</a>. Your notice must include:
          </p>
          <ul>
            <li>A physical or electronic signature of the copyright owner or authorized representative;</li>
            <li>Identification of the copyrighted work claimed to have been infringed;</li>
            <li>Identification of the material claimed to be infringing and its location on the Site;</li>
            <li>Your contact information (name, address, phone number, email);</li>
            <li>A statement that you have a good faith belief that the use is not authorized;</li>
            <li>A statement that the information in your notice is accurate and that you are authorized to act on behalf of the copyright owner.</li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default TermsPage;
